"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useLatest } from "@/hooks/useLatest";
import { verifyIncentiveTask, type IncentiveVerifyResponse } from "@/lib/api/incentive";

// Smart polling for external incentive tasks.
//
// Strategy: aggressive at first (user just clicked button → likely to act now),
// relaxed after, background after long idle. Combined with Page Visibility API
// (frontend pauses polling when tab hidden) → typical 1-3 calls per user task.
//
// Cost analysis @ twitterapi.io ($0.001/call):
//   - 200 orgs/month: ~$0.20-0.60
//   - 10k orgs/month: ~$10-30
//
// Edge cases handled:
//   1. User clicks button, opens external tab → dashboard hidden → polling pauses
//   2. User comes back to dashboard → visibilitychange → fire 1 verify immediately
//   3. User opens dashboard but never clicks button → no polling at all (caller controls `enabled`)
//   4. Multi-tab: only the visible tab polls (browser fires visibilitychange only on visible tab)
//   5. Mobile iOS: tab may be suspended → polling pauses, resumes on visibility change
//
// Polling intervals:
//   0–30s:   2s/poll  (aggressive, capture quick follow-and-return)
//   30s–2m:  10s/poll (relaxed)
//   2m–10m:  30s/poll (background)
//   >10m:    stop (cap to prevent runaway)

const POLL_INTERVALS = [{
  until: 30_000,
  interval: 2_000
}, {
  until: 120_000,
  interval: 10_000
}, {
  until: 600_000,
  interval: 30_000
}] as const;
export const POLL_TIMEOUT_MS = 10 * 60 * 1000;
export type { IncentiveVerifyResponse } from "@/lib/api/incentive";
export interface UseIncentivePollingOpts {
  taskType: string;
  enabled: boolean;
  onClaimed: (resp: IncentiveVerifyResponse) => void;
  onFailure?: (resp: IncentiveVerifyResponse) => void;
  // Fires once when polling exits the timeout cap without a claim. UI uses
  // this to surface the manual "Followed but not detected? Tap to refresh"
  // hint — it should NOT appear after a single transient failure.
  onTimeout?: () => void;
}
export function useIncentivePolling(opts) {
  const $ = _c(9);
  const {
    taskType,
    enabled,
    onClaimed,
    onFailure,
    onTimeout
  } = opts;
  const startRef = useRef(0);
  const stoppedRef = useRef(false);
  const onClaimedRef = useLatest(onClaimed);
  const onFailureRef = useLatest(onFailure);
  const onTimeoutRef = useLatest(onTimeout);
  let t0;
  if ($[0] !== enabled || $[1] !== onClaimedRef || $[2] !== onFailureRef || $[3] !== onTimeoutRef || $[4] !== taskType) {
    t0 = () => {
      if (!enabled) {
        return;
      }
      stoppedRef.current = false;
      startRef.current = Date.now();
      const verifyOnce = async () => {
        if (stoppedRef.current) {
          return;
        }
        try {
          const data = await verifyIncentiveTask(taskType);
          if (data.status === "claimed") {
            stoppedRef.current = true;
            onClaimedRef.current(data);
          } else {
            if (onFailureRef.current) {
              onFailureRef.current(data);
            }
          }
        } catch {}
      };
      const onVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          verifyOnce();
        }
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      let pendingTimer;
      pendingTimer = 0;
      const scheduleNext = () => {
        if (stoppedRef.current) {
          return;
        }
        const elapsed = Date.now() - startRef.current;
        if (elapsed > POLL_TIMEOUT_MS) {
          stoppedRef.current = true;
          onTimeoutRef.current?.();
          return;
        }
        const tier = POLL_INTERVALS.find(t => elapsed < t.until) ?? POLL_INTERVALS[POLL_INTERVALS.length - 1];
        pendingTimer = window.setTimeout(async () => {
          pendingTimer = 0;
          if (document.visibilityState === "visible") {
            await verifyOnce();
          }
          scheduleNext();
        }, tier.interval);
      };
      scheduleNext();
      return () => {
        stoppedRef.current = true;
        if (pendingTimer) {
          window.clearTimeout(pendingTimer);
        }
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
    };
    $[0] = enabled;
    $[1] = onClaimedRef;
    $[2] = onFailureRef;
    $[3] = onTimeoutRef;
    $[4] = taskType;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== enabled || $[7] !== taskType) {
    t1 = [enabled, taskType];
    $[6] = enabled;
    $[7] = taskType;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  useEffect(t0, t1);
}